import React from 'react';
import { Button } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link } from 'react-router-dom';
import {
  APP_PROGRESS_STAGE,
  DISPLAYABLE_PROGRESS_STAGES,
  TOTAL_NUMBER_OF_APP_PROGRESS_STEPS,
} from './batchProgressConstants';
import { useListenForBatchProgress } from './batchProgressEventStream';
import { DashboardContext, DomainContext } from '../dashboards/eo/revenueUplift/revenueUpliftContexts';
import { localizedDateFormat, localizedToday } from '../dashboards/eo/revenueUplift/atAGlanceTabUtil';
import { utcDate } from '../dashboards/eo/revenueUplift/relativeDates';
import ProfitRoverSpinner from '../spinner/ProfitRoverSpinner';
import './workflow-progress.scss';

const WorkflowProgress = ({ batch, workflowId }) => {
  const { locationTimezone, processedHistorySummaryMeta } = React.useContext(DomainContext);
  const { locale } = React.useContext(DashboardContext);

  const progressStage = useListenForBatchProgress(workflowId, batch);
  const { stage, step, completionPct } = progressStage;

  const today = localizedToday(locationTimezone);
  const lastRefreshedDate = React.useMemo(() => {
    const { as_of_date: asOfDate } = processedHistorySummaryMeta;

    return utcDate(asOfDate);
  }, [processedHistorySummaryMeta]);

  const outdated = lastRefreshedDate < today && stage === APP_PROGRESS_STAGE.COMPLETE;

  // It is possible for a batch to be complete but progress is not
  // this is most likely due to an error mid-batch (i.e. before progress units are started and/or finished)
  const midBatchProgressError =
    batch &&
    !batch.progress_complete &&
    (batch.abort || batch.error) &&
    !batch.batch_progress.is_progress_unit_creation_complete;

  const isUpdated =
    stage === APP_PROGRESS_STAGE.COMPLETE || (stage === APP_PROGRESS_STAGE.UPDATING_RESULTS && completionPct === 100);

  const stageLabel = isUpdated ? 'Up-To-Date' : DISPLAYABLE_PROGRESS_STAGES.find(({ value }) => value === stage)?.label;

  const getStageProgress = () => {
    if (isUpdated) {
      return '';
    }

    if (stage === APP_PROGRESS_STAGE.PREPARING_DATA && step >= 1 && step <= TOTAL_NUMBER_OF_APP_PROGRESS_STEPS) {
      return `: Step ${step} of ${TOTAL_NUMBER_OF_APP_PROGRESS_STEPS}`;
    }

    if (stage === APP_PROGRESS_STAGE.UPDATING_RESULTS && completionPct >= 0 && completionPct <= 100) {
      return `: ${completionPct}%`;
    }

    return '';
  };

  const refreshPage = () => {
    window.location.reload();
  };

  // Only show refresh button if a batch was completed since the dashboard was loaded
  const showRefreshButton = isUpdated && batch != null;

  if (midBatchProgressError) {
    return (
      <div className="workflow-status-container">
        <ErrorIcon className="error-icon" />
        <span className="label">
          Refresh Error - <Link to="/help">Contact Us</Link>
        </span>
      </div>
    );
  }

  if (outdated) {
    return (
      <div className="workflow-status-container">
        <WarningIcon className="warning-icon" />
        <span className="label">
          Missing data since {localizedDateFormat(lastRefreshedDate, locationTimezone, locale, 'full')}
        </span>
      </div>
    );
  }

  return (
    <div className="workflow-progress-container">
      {isUpdated ? <CheckCircleIcon className="success-icon" /> : <ProfitRoverSpinner className="loader" />}
      <div className="progress-stage-label">
        {stageLabel}
        {getStageProgress()}
      </div>
      {showRefreshButton && (
        <Button
          variant="outlined"
          className="refresh-button"
          startIcon={<RefreshIcon className="icon" />}
          onClick={refreshPage}
        >
          Refresh
        </Button>
      )}
    </div>
  );
};

export default WorkflowProgress;
